import { Elements } from '@stripe/react-stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useEventContext } from '../../app/provider/useEventContext';
import { useToast } from '../../app/provider/useToast';
import { appConfig } from '../../config/app-config';
import { PLACE_ORDER_INVOICE, CREATE_PAYMENT_INTENT, PAYMENT_CREATE } from '../../shared/constants/api';
import * as api from '../../shared/services/apiClient';
import { updateCart } from '../cart/api/cartApi';
import { getCart } from '../cart/services/getCart';
import {
  isInvoicePaymentAvailable,
  isEventWithinInvoiceCutoff,
  isCardPaymentFailureEligibleForInvoice
} from '../cart/utils/cartEligibility';
import { groupLineItemsByAssociation } from '../cart/utils/cartGrouping';
import { CheckoutDiscountProvider } from '../discount/context/CheckoutDiscountContext';
import { useCheckoutDiscounts } from '../discount/hooks/useCheckoutDiscounts';
import { PAYMENT_METHOD, ORDER_CT_ACTIONS, ORDER_CT_TYPES } from '../order/constants/order.constants';
import { getOrderById, updateOrderCustomFields } from '../order/services/order.service';
import { getProductStoreMap } from '../store/utils/productStoreMap';
import {
  buildTaxedPriceActions,
  buildTaxCustomFieldActions,
  buildExactTaxRequest,
  callTaxApiDirect,
  normalizeTaxResult,
  fetchProductDescriptions,
  fetchCategories,
  fetchStores,
  getTaxForSummary,
  useTax,
} from '../tax';

import CheckoutErrorBanner from './CheckoutErrorBanner';
import CheckoutHeader from './CheckoutHeader';
import BillingDetails from './billing-details/BillingDetails.jsx';
import BillingDetailsFilled from './billing-details/BillingDetailsFilled.jsx';
import { getAppliedDiscountsForSummary } from './checkoutSummaryHelpers';
import OrderSummary from './order-summary/order-summary';
import InvoicePaymentForm from './payment-method/Invoice/InvoicePaymentForm';
import StripePaymentForm from './payment-method/StripePayment/StripePaymentForm';
import { useCheckoutStripe } from './payment-method/StripePayment/useCheckoutStripe';
import { useCheckoutAttendees } from './useCheckoutAttendees';
import { useCheckoutCart } from './useCheckoutCart';
import { useCheckoutTerms } from './useCheckoutTerms';
import './checkout-page.css';



const CheckoutPage = () => {
  const navigate = useNavigate();
  const { storeKey, eventKey: rawEventKey } = useParams();
  const eventKey = rawEventKey || appConfig.defaults.eventKey;
  const resolvedStoreKey = storeKey || appConfig.defaults.storeKey;
  const { showSuccess, showError } = useToast();
  const { eventData, refetchEventData } = useEventContext();
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingPayLater, setProcessingPayLater] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [_piId, setPiId] = useState(null);
  const [cardPaymentFailed, setCardPaymentFailed] = useState(false);

  // Address & Tax states (delegated to tax module hook)
  const { taxSummary, setTaxSummary } = useTax();
  const [address, setAddress] = useState(null);
  const [addressSaved, setAddressSaved] = useState(false);

  // Billing details states
  const [showBillingForm, setShowBillingForm] = useState(true);
  const [savedBillingData, setSavedBillingData] = useState(null);

  // Discount code state (owned by page so it can be passed to useCheckoutCart and useCheckoutDiscounts)
  const [discountError, setDiscountError] = useState(null);

  // New Production-Grade Hooks for encapsulated state
  const {
    attendeesData,
    attendeeFormErrors,
    validateAttendees: checkAttendees,
    buildAttendeeSplitActions: getAttendeeActions,
  } = useCheckoutAttendees();

  const {
    termsAccepted,
    setTermsAccepted,
    showTermsError,
    validateTerms: checkTerms,
  } = useCheckoutTerms();

  // Payment Method State
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.CARD);

  const {
    cart,
    loading,
    error: cartError,
    setCart,
    setError: setCartError,
    setLoading: setInternalLoading,
    loadCart,
    validateCartDiscounts,
  } = useCheckoutCart({
    navigate,
    eventKey,
    setClientSecret,
    setPiId,
    setDiscountError,
  });

  const setLoading = setInternalLoading;

  // Compute display error including validation errors from new hooks
  const hasAttendeeErrors = Object.keys(attendeeFormErrors).length > 0;
  const displayError = error || cartError || (showTermsError ? 'Please accept the Terms & Conditions' : null) || (hasAttendeeErrors ? 'Please check attendee details' : null);

  const currency = sessionStorage.getItem('currency') || 'USD';

  const { stripePromise, elementsOptions } = useCheckoutStripe(clientSecret);

  const {
    applyingDiscount,
    handleApplyDiscount,
    handleRemoveDiscount,
  } = useCheckoutDiscounts({
    updateCart,
    getCart,
    setCart,
    setDiscountError,
    setClientSecret,
    setPiId,
    setTaxSummary,
    setError,
  });

  useEffect(() => {
    loadCart();
    if (refetchEventData) {
      refetchEventData();
    }
  }, [loadCart, refetchEventData]);


  // Transform line items to group add-ons with their parent products
  const transformedLineItems = useMemo(() => {
    if (!cart || !cart.lineItems) return [];

    // Merge priceData from taxSummary if available
    let itemsToProcess = cart.lineItems;
    if (taxSummary && taxSummary.lineItems && taxSummary.lineItems.length === cart.lineItems.length) {
      itemsToProcess = cart.lineItems.map((item, idx) => {
        const taxItem = taxSummary.lineItems[idx];
        // Ensure we only merge if it matches (or just trust index)
        // User JSON shows lineReference 1..N matching cart order usually.
        return {
          ...item,
          priceData: taxItem?.priceData || item.priceData // prioritize tax API data
        };
      });
    }

    // Use backend's groupedItems if available (correctly handles multi-buy discount splits)
    // Transform to match the structure expected by LineItemsPreview
    if (cart.groupedItems) {
      // Create priceData map for easier lookup
      const priceDataMap = {};
      if (taxSummary && taxSummary.lineItems && cart.lineItems) {
        cart.lineItems.forEach((item, idx) => {
          const taxVal = taxSummary.lineItems[idx];
          if (taxVal && taxVal.priceData) {
            priceDataMap[item.id] = taxVal.priceData;
          }
        });
      }

      return cart.groupedItems.map(group => ({
        id: group.mainItem?.id,
        productId: group.mainItem?.productId,
        name: typeof group.mainItem?.name === 'string'
          ? { 'en': group.mainItem.name }  // Wrap string in locale object
          : group.mainItem?.name,
        quantity: group.mainItem?.quantity || 0,
        price: {
          value: {
            centAmount: group.passesSubtotal || group.passesTotal || 0, // Prefer Gross Total, fallback to Total (Net)
            currencyCode: group.currency || currency
          }
        },
        totalPrice: {
          centAmount: group.groupSubtotal || group.passesSubtotal || group.passesTotal || group.totalPrice || 0,
          currencyCode: group.currency || currency
        },
        custom: group.mainItem?.custom,
        priceData: group.mainItem ? priceDataMap[group.mainItem.id] : undefined,
        addons: (group.addOns || []).map(addon => ({
          name: addon.name,
          price: {
            centAmount: addon.price || 0, // Pass unit price, component will multiply by qty
            currencyCode: addon.currency || currency
          },
          priceData: priceDataMap[addon.id]
        }))
      }));
    }

    // Fallback to frontend grouping
    return groupLineItemsByAssociation(itemsToProcess, {}, currency);
  }, [cart, currency, taxSummary]);

  // ecom-ui loadCart
  // const loadCart = async () => {
  //   try {
  //     setLoading(true);
  //     const cartData = await getCart();

  //     if (!cartData || !cartData.lineItems || cartData.lineItems.length === 0) {
  //       navigate('/cart');
  //       return;
  //     }

  //     setCart(cartData);

  //     setError('Failed to load cart');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ---------- COMMON CART TOTALS HELPER ----------
  // ---------- COMMON CART TOTALS HELPER ----------


  // ---------- ADDRESS SAVE HANDLER ----------
  const handleAddressSave = async (formData, overrideCart = null) => {
    setLoading(true);
    setError(null);
    try {
      // Since we're collecting billing address, use formData as billing address
      // Shipping address can be same as billing for digital/event products
      const billingAddr = formData.billingAddress || formData;
      const shippingAddr = formData.shippingAddress || formData; // Use billing as shipping if not provided


      // Get customer email and profile ID from ping user info or localStorage
      let customerEmail = null;
      let userProfileId = null;
      let userFirstName = null;
      let userLastName = null;
      const pingUserStr = sessionStorage.getItem('ping_user_info') || localStorage.getItem('ping_user_info');

      if (pingUserStr) {
        try {
          const userInfo = JSON.parse(pingUserStr);
          customerEmail = userInfo.email;
          userProfileId = userInfo.user_profile_id || userInfo.sub; // Try 'sub' as fallback
          userFirstName = userInfo.given_name;
          userLastName = userInfo.family_name;
        } catch (e) {
          console.warn('[checkout] Could not parse ping_user_info JSON — falling back to localStorage:', e);
          // Malformed ping_user_info JSON — fall through to localStorage lookup
        }
      }
      if (!customerEmail) {
        customerEmail = localStorage.getItem('ping_email');
      }

      // Step 1: Get current cart (use overrideCart to avoid fetching stale data)
      const currentCart = overrideCart || await getCart();
      if (!currentCart) throw new Error('Failed to retrieve cart');

      // Step 2: Critical - Sync prices BEFORE tax calculation
      // This ensures we're calculating tax on accurate, up-to-date prices
      // If prices changed, we redirect to cart for user review
      const { applyDiscount } = await import('../cart/services/applyDiscount');


      try {
        const syncResult = await applyDiscount(currentCart.id);

        if (syncResult.priceChanges && syncResult.priceChanges.length > 0) {
          // Prices changed - notify user and redirect to cart for review
          showSuccess('Prices have been updated. Please review your cart.');

          setLoading(false);

          // Navigate back to cart page after brief delay for toast visibility
          setTimeout(() => {
            navigate(`/${resolvedStoreKey}/cart`);
          }, 1500);

          // Throw controlled error to prevent further processing
          throw new Error('PRICE_CHANGED');
        }
      } catch (syncError) {
        // If it's our controlled price change error, re-throw to caller
        if (syncError.message === 'PRICE_CHANGED') {
          throw syncError;
        }

        // Otherwise it's a real sync error - notify user and block
        showError('Unable to verify prices. Please try again.');
        setLoading(false);
        throw syncError;
      }

      // Step 3: Store addresses in local state (avoid CT validation before tax is applied)
      setAddress(formData);
      setAddressSaved(true);

      // Step 3: Calculate tax from custom API using form addresses
      try {
        // Use formData as billing address (what user entered)
        const billingAddrInput = billingAddr;
        const shippingAddrInput = shippingAddr;

        // Fetch product descriptions for tax request
        const productDescriptions = await fetchProductDescriptions(currentCart);
        const categories = await fetchCategories(currentCart);
        const stores = await fetchStores(currentCart);

        const taxBody = buildExactTaxRequest(
          currentCart,
          billingAddrInput,
          shippingAddrInput,
          productDescriptions,
          userProfileId,
          categories,
          stores
        );
        const taxResult = await callTaxApiDirect(taxBody);

        const normalized = normalizeTaxResult(taxResult, currentCart);

        // Step 4: Send addresses + customer email + tax together to commercetools
        const combinedActions = [];

        // FIRST: Change tax mode to 'External' if not already
        if (currentCart.taxMode !== 'External') {
          combinedActions.push({ action: 'changeTaxMode', taxMode: 'External' });
        }

        // Add customer email if available
        if (customerEmail) {
          combinedActions.push({ action: 'setCustomerEmail', email: customerEmail });
        }

        // Add tax for each line item BEFORE setting addresses
        // Pass the billing address country (or shipping) as preferred country for tax rate
        const taxActions = buildTaxedPriceActions(normalized, currentCart, billingAddr.country || shippingAddr.country);
        if (taxActions.length > 0) combinedActions.push(...taxActions);

        // Persist tax summary fields (erpTaxCode, natureOfTax, etc.) as line item custom fields
        const taxFieldActions = buildTaxCustomFieldActions(normalized, currentCart);
        if (taxFieldActions.length > 0) combinedActions.push(...taxFieldActions);

        // Then add addresses (after tax is set)
        combinedActions.push(
          {
            action: 'setBillingAddress',
            address: {
              firstName: billingAddr.firstName || userFirstName,
              lastName: billingAddr.lastName || userLastName,
              streetName: billingAddr.streetName,
              streetNumber: billingAddr.houseNumber,
              city: billingAddr.city,
              postalCode: billingAddr.postalCode,
              country: billingAddr.country,
              state: billingAddr.state || undefined,
              phone: billingAddr.phone || undefined,
              company: billingAddr.company || undefined,
              custom: {
                type: {
                  key: 'addressAdditionalFields',
                  typeId: 'type',
                },
                fields: {
                  countryName: billingAddr.countryName,
                  stateCode: '',
                  taxNumber: billingAddr.taxNumber || ' '
                },
              },
            },
          },
          {
            action: 'setShippingAddress',
            address: {
              firstName: shippingAddr.firstName || userFirstName,
              lastName: shippingAddr.lastName || userLastName,
              streetName: shippingAddr.streetName,
              streetNumber: shippingAddr.houseNumber,
              city: shippingAddr.city,
              postalCode: shippingAddr.postalCode,
              country: shippingAddr.country,
              state: shippingAddr.state || undefined,
              phone: shippingAddr.phone || undefined,
              company: shippingAddr.company || undefined,
              custom: {
                type: {
                  key: 'addressAdditionalFields',
                  typeId: 'type',
                },
                fields: {
                  countryName: shippingAddr.countryName,
                  stateCode: '',
                  taxNumber: shippingAddr.taxNumber || ' '
                },
              },
            },
          }
        );

        try {
          const updatedCart = await updateCart(combinedActions);
          if (updatedCart) {
            setCart(updatedCart);
          }
        } catch (updateErr) {
          console.error('[checkout] Failed to update cart with tax and address:', updateErr);
          setError('Failed to update cart. Please try again.');
        }


        setTaxSummary(normalized);
        return normalized;

      } catch (taxErr) {
        console.error('[checkout] Tax calculation failed:', taxErr);
        setTaxSummary(null);
        setError('Tax calculation failed. Please try again.');
        return null;
      }
    } catch (e) {
      setError(`Failed to save address: ${e.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
    // Return null if failed or no tax result
    return null;
  };

  // ---------- PAYMENT HANDLER (with attendee validation & external tax) ----------

  // Handler that combines address save (tax calculation) + payment setup
  const handleBillingContinue = async (addressData) => {

    if (!addressData || !addressData.country) {
      setError('Please provide a valid address');
      return;
    }

    // Format address data for handleAddressSave
    const formattedAddress = {
      firstName: addressData.firstName || '',
      lastName: addressData.lastName || '',
      streetName: addressData.line1 || addressData.streetName || '',
      houseNumber: addressData.line2 || addressData.houseNumber || '',
      city: addressData.city || '',
      postalCode: addressData.postcode || addressData.zip || addressData.postalCode || '',
      country: addressData.country || 'GB',
      countryName: addressData.countryName || 'United Kingdom',
      state: addressData.state || addressData.province || '',
      phone: addressData.phone || '',
      company: addressData.companyName || addressData.company || '',
      taxNumber: addressData.taxNumber || ''
    };

    // Step 1: Validate discounts before moving to payment
    const validationResult = await validateCartDiscounts();
    let currentValidatedCart = cart;

    if (validationResult.shouldStop) {
      // Something was removed. Update the local cart state and show error immediately.
      currentValidatedCart = validationResult.cart || cart;
      setCart(currentValidatedCart);

      // Stop right here, don't execute handleAddressSave or proceed
      if (validationResult.message) {
        setError(validationResult.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    // Call handleAddressSave to sync prices and calculate tax
    // Use the potentially updated currentValidatedCart
    try {
      await handleAddressSave(formattedAddress, currentValidatedCart);
    } catch (error) {
      // If price changed, don't proceed with hiding the form - user will be redirected
      if (error.message === 'PRICE_CHANGED') {
        return;
      }
      // For other errors, also stop processing
      return;
    }

    // If discount was removed, let the user know now that they are looking at the new total
    if (validationResult.shouldStop && validationResult.message) {
      setError(validationResult.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Only proceed to hide form if no errors occurred
    // Save billing data for display in filled view
    const billingDataForDisplay = {
      billingAddress: `${formattedAddress.streetName}${formattedAddress.houseNumber ? ' ' + formattedAddress.houseNumber : ''},\n${formattedAddress.city}, ${formattedAddress.state}\n${formattedAddress.countryName || formattedAddress.country}, ${formattedAddress.postalCode}`,
      companyName: addressData.companyName || formattedAddress.company || '-',
      phoneNumber: addressData.phone || formattedAddress.phone || '-',
      purchaseOrderNumber: addressData.poNumber || addressData.purchaseOrderNumber || '-',
      taxNumber: addressData.taxNumber || '-'
    };

    setSavedBillingData(billingDataForDisplay);

    // Force refresh event config before showing payment methods
    // This ensures invoice availability check uses latest merchant config
    if (refetchEventData) {
      refetchEventData(true); // forceRefresh = true to bypass TTL
    }

    setShowBillingForm(false); // Switch to filled view

    // After tax is calculated, handleAddressSave will update taxSummary state
    // We stop here and wait for user to select payment method and click "Pay Now"
  };

  const _handleInvoiceCheckout = async () => {
    // validate terms first using the hook
    if (!checkTerms()) {
      return;
    }

    setProcessingPayment(true);
    setError(null);

    try {
      // Step 1: Final validation before placing order
      const validationResult = await validateCartDiscounts();
      if (validationResult.shouldStop) {
        if (validationResult.cart) setCart(validationResult.cart);
        // Force tax update for new total visibility
        if (address && addressSaved) {
          await handleAddressSave(address, validationResult.cart);
        }
        if (validationResult.message) {
          setError(validationResult.message);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setShowBillingForm(true); // Retreat to the previous stage
        setProcessingPayment(false);
        return;
      }
      // Fetch latest cart version to avoid ConcurrentModificationError
      const latestCart = await getCart();
      if (!latestCart) {
        throw new Error('Failed to retrieve fresh cart data');
      }

      const response = await api.post(PLACE_ORDER_INVOICE, {
        cartId: latestCart.id,
        cartVersion: latestCart.version,
        invoiceConfig: eventData ? {
          paymentMethodsConfig: eventData.paymentMethodsConfig,
          invoiceMinThreshold: eventData.invoiceMinThreshold,
          invoiceCutoffDays: eventData.invoiceCutoffDays,
          startDate: eventData.startDate
        } : null
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to place invoice order');
      }

      const data = await response.json();

      // Redundant update removed: Amber Country and Legal Entity fields are now 
      // handled atomically on the confirmation page or via the consolidated update flow.

      // Navigate to invoice confirmation page
      navigate(`/${resolvedStoreKey}/invoice-confirmation?orderId=${data.orderId}`);

    } catch (err) {
      setError(err.message || 'Failed to place order');
      setProcessingPayment(false);
    }
  };

  const handleCheckout = async (skipValidation = false) => {
    // Reset failure state on new attempt to allow retries or different outcomes
    setCardPaymentFailed(false);


    // Step 1: Validate discounts before payment
    const validationResult = await validateCartDiscounts();

    // If validation changed the cart, we need to handle it
    if (validationResult.shouldStop) {
      const newCart = validationResult.cart;
      if (newCart) {
        // Update state
        setCart(newCart);

        // CRITICAL: Re-calculate tax immediately using the NEW cart and EXISTING address.
        // This ensures the "Total" is correct (Subtotal + Tax) immediately.
        if (address && addressSaved) {
          const forcedTaxResult = await handleAddressSave(address, newCart);
          if (!forcedTaxResult) {
            setTaxSummary(null); // Fallback
          }
        } else {
          setTaxSummary(null);
        }
      }
      // Set error message AFTER handleAddressSave, because handleAddressSave clears errors internally
      if (validationResult.message) {
        setError(validationResult.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setShowBillingForm(true); // Retreat to the previous stage
      return;
    }
    const validatedCart = validationResult.cart || cart;

    // Step 2: Ensure tax summary exists before payment (double check)
    // If missing, try to calculate it now
    if (!taxSummary && address && addressSaved) {
      const result = await handleAddressSave(address, validatedCart);
      if (!result) {
        setError('Tax calculation is required before payment. Please try again.');
        return;
      }
    } else if (!taxSummary) {
      setError('Tax calculation is required before payment. Please try again.');
      return;
    }

    // Only validate attendees if not skipping validation and attendee data exists
    const hasAnyAttendeeData = Object.keys(attendeesData).length > 0;

    if (!skipValidation && hasAnyAttendeeData) {
      const { hasErrors } = checkAttendees(cart);
      if (hasErrors) {
        return;
      }
    }

    try {
      const cartUpdateActions = getAttendeeActions(cart);

      // Update cart with split line items if there are attendees
      let finalCart = cart;
      if (cartUpdateActions.length > 0) {
        try {
          finalCart = await updateCart(cartUpdateActions);
          if (!finalCart) {
            throw new Error('Cart update returned null');
          }
          // Reload cart to get fresh data with new line item IDs
          const reloadedCart = await getCart();
          if (reloadedCart) {
            finalCart = reloadedCart;
          }

          // CRITICAL: Re-apply external tax to the NEW line items created after attendee split
          if (taxSummary && taxSummary.taxRate && finalCart.lineItems && finalCart.lineItems.length > 0) {
            const taxRate = taxSummary.taxRate;
            const shippingCountry = finalCart.shippingAddress?.country || finalCart.country || 'US';

            // Apply the same tax rate to all new line items
            const taxReapplyActions = finalCart.lineItems.map((item) => ({
              action: 'setLineItemTaxRate',
              lineItemId: item.id,
              externalTaxRate: {
                name: 'Custom Tax',
                amount: taxRate,
                country: shippingCountry,
                includedInPrice: false,
              },
            }));

            // Also re-apply tax custom fields to split line items
            const taxFieldReapplyActions = buildTaxCustomFieldActions(taxSummary, finalCart);
            if (taxFieldReapplyActions.length > 0) {
              taxReapplyActions.push(...taxFieldReapplyActions);
            }

            if (taxReapplyActions.length > 0) {
              finalCart = await updateCart(taxReapplyActions);
            }
          }
        } catch (cartUpdateErr) {
          console.error('[checkout] Failed to update cart with attendee split actions:', cartUpdateErr);
          setError('Failed to save attendee information.');
          setProcessingPayment(false);
          return;
        }
      }

      // Ensure we have a valid cart before proceeding
      if (!finalCart || !finalCart.id) {
        setError('Cart is invalid. Please try again.');
        setProcessingPayment(false);
        return;
      }

      // Inline payment flow: create Payment Intent and render Elements
      //   cartId: finalCart.id,
      //   taxRate: taxSummary?.taxRate,
      //   grandTotal: taxSummary?.grandTotal,
      // });

      const productStoreMap = getProductStoreMap();
      const response = await api.post(CREATE_PAYMENT_INTENT, {
        cartId: finalCart.id,
        productStoreMap,
        taxSummary,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create payment intent');
      }
      const data = await response.json();

      // Update state, but also return the data for the immediate caller
      setClientSecret(data.clientSecret);
      setPiId(data.paymentIntentId);
      setProcessingPayment(false);

      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId
      };

    } catch (err) {
      setError(err.message || 'Failed to process checkout');
      setProcessingPayment(false);
      throw err; // Re-throw so caller knows it failed
    }
  };

  if (loading) {
    return (
      <div className="checkout-container">
        <div className="checkout-loading">
          <div className="checkout-spinner"></div>
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!cart) {
    return (
      <div className="checkout-container">
        <div className="checkout-empty">
          <h2>No items to checkout</h2>
          <button onClick={() => navigate(`/${resolvedStoreKey}/${eventKey}`)} className="btn-primary">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // Gate the entire page rendering when transitioning to payment step
  // Wait for clientSecret (Stripe Payment Intent) to be ready before showing ANYTHING
  // MODIFIED: We no longer wait for clientSecret to show payment form, as we generate it on "Pay Now"
  // if (!showBillingForm && !clientSecret && !error) {
  //   return (
  //     <div className="checkout-container">
  //       <div className="checkout-loading">
  //         <div className="checkout-spinner"></div>
  //         <p>Preparing secure payment...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // storeBreakdown keys computed but not used for multi-store splitting currently
  // const { subtotalBeforeDiscount, discountAmount, subtotal } = computeCartTotals(cart);
  // USE BACKEND TOTALS DIRECTLY
  // cart.subtotal is Gross (sum of prices). cart.totalDiscount is Discount.
  const subtotalBeforeDiscount = (cart.subtotal || 0) / 100;
  // Safety: if any discount codes on the cart are inactive or invalid, ignore cart.totalDiscount
  // We check for MatchesCart AND isActive !== false AND presence of expanded obj
  const hasAppliedDiscounts = cart.discountCodes && cart.discountCodes.length > 0;
  const hasOnlyValidDiscountCodes = hasAppliedDiscounts && cart.discountCodes.every(
    (dc) => dc.state === 'MatchesCart' &&
      dc.discountCode?.obj && // Must be expanded to be considered valid
      dc.discountCode.obj.isActive !== false
  );

  // If there's even one invalid code, we might have stale totals, so we default to 0 discount until recalculation finishes
  const discountAmount = hasOnlyValidDiscountCodes ? (cart.totalDiscount || 0) / 100 : 0;
  // Net subtotal (before tax)
  const subtotal = subtotalBeforeDiscount - discountAmount;

  return (
    <CheckoutDiscountProvider value={{
      applyingDiscount,
      discountError,
      setDiscountError,
      handleApplyDiscount,
      handleRemoveDiscount
    }}>
      <>
        <div className="checkout-container">
          {/* <ContextDisplay /> */}

          {/* Calculate corrected total to handle stale CT data */}
          {/* If computeCartTotals says subtotal is X, we should trust it over cart.totalPrice if they differ */}
          {(() => {
            // We can use the values already computed above from computeCartTotals(cart)
            // const { subtotalBeforeDiscount, discountAmount, subtotal } = computeCartTotals(cart);

            // Construct corrected total object
            // subtotal from computeCartTotals includes discount logic but EXCLUDES tax
            // If taxSummary exists, grandTotal handles it.
            // If taxSummary missing, we used to show cart.totalPrice.
            // Now we show strict Subtotal.

            // BUT cart.totalPrice usually includes Tax? 
            // In External Tax Mode, cart.totalPrice usually excludes Tax until tax is added?
            // Actually, subtotal from computeCartTotals is Net Total (Price - Discount).

            // So correctedTotal should be subtotal.
            // correctedTotal computed in IIFE for reference only — applies subtotal logic

            return null; // Just logic block, actually applied below in prop
          })()}

          <CheckoutHeader onBack={() => navigate(`/${resolvedStoreKey}/cart`)} />

          {/* Mobile Order Summary - Visible only on mobile (< 576px) */}
          <div className="min-[576px]:hidden w-full max-w-[350px] mx-auto mb-4">
            <OrderSummary
              lineItems={transformedLineItems}
              subtotal={{ centAmount: Math.round(subtotalBeforeDiscount * 100), currencyCode: cart.currency || 'USD' }}
              totalDiscount={{ centAmount: Math.round(discountAmount * 100), currencyCode: cart.currency || 'USD' }}
              total={{
                centAmount: Math.round(subtotal * 100),
                currencyCode: cart.currency || 'USD'
              }}
              tax={getTaxForSummary(taxSummary, subtotal)}
              appliedDiscounts={getAppliedDiscountsForSummary(cart, discountAmount, subtotalBeforeDiscount)}
              showActiveCodes={true}
              disabled={loading}
              placeholder="Enter discount code"
              buttonText="Apply"
            />
          </div>

          {displayError && (
            <CheckoutErrorBanner
              message={displayError}
              onDismiss={() => { setError(null); setCartError(null); }}
            />
          )}

          <div className="checkout-layout">
            {/* Billing Details / Payment Form - Left Side */}
            <div className="checkout-billing-section">
              {showBillingForm ? (
                // Show billing form initially or when editing
                <BillingDetails
                  onContinueToPayment={handleBillingContinue}
                  initialData={
                    savedBillingData ? {
                      // Address fields
                      ...address,
                      // Your details fields from savedBillingData
                      companyName: savedBillingData.companyName !== '-' ? savedBillingData.companyName : '',
                      phone: savedBillingData.phoneNumber !== '-' ? savedBillingData.phoneNumber : '',
                      phoneNumber: savedBillingData.phoneNumber !== '-' ? savedBillingData.phoneNumber : '',
                      poNumber: savedBillingData.purchaseOrderNumber !== '-' ? savedBillingData.purchaseOrderNumber : '',
                      purchaseOrderNumber: savedBillingData.purchaseOrderNumber !== '-' ? savedBillingData.purchaseOrderNumber : '',
                      taxNumber: savedBillingData.taxNumber !== '-' ? savedBillingData.taxNumber : ''
                    } : address
                  }
                />
              ) : (
                // Show payment form immediately after billing is complete
                <div className="payment-form-container">
                  <BillingDetailsFilled
                    billingData={savedBillingData}
                    onEdit={() => {
                      setShowBillingForm(true);
                      setTaxSummary(null);
                      setClientSecret(null);
                      setPiId(null);
                      setProcessingPayment(false);
                    }}
                  />

                  {/* Unified Payment Methods Section */}
                  {/* eslint-disable-next-line no-constant-condition */}
                  {(!error && !processingPayment && !processingPayLater && false) ? (
                    // Disabled this loading state since we don't auto-load payment intent anymore
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="checkout-spinner mb-4"></div>
                      <p className="text-gray-500">Loading payment options...</p>
                    </div>
                  ) : (
                    <div className="w-full max-w-[588px] max-[576px]:max-w-[350px] mx-auto mt-6 mb-12 pt-8 border-t border-[#EAECF0] flex flex-col" style={{ fontFamily: 'Nano Sans, sans-serif' }}>
                      <div>
                        <h2 className="font-aleo font-semibold text-[26px] leading-[32px] text-[#182230] mb-[4px] w-[462px] max-[576px]:w-full max-[576px]:max-w-[350px]">Payment methods</h2>
                        <p className="font-noto font-normal text-[14px] leading-[24px] text-[#667085] mb-[24px] w-[462px] max-[576px]:w-full max-[576px]:max-w-[350px]">
                          Choose your payment method to complete your purchase.
                        </p>
                      </div>

                      {/* CARD PAYMENT METHOD */}
                      <div className="flex flex-col gap-4">
                        {/* Credit Card Section - Wrapped in Elements */}
                        {stripePromise && elementsOptions && (
                          <Elements stripe={stripePromise} options={elementsOptions}>
                            <StripePaymentForm
                              clientSecret={clientSecret}
                              expanded={paymentMethod === 'card'}
                              onToggle={() => setPaymentMethod('card')}
                              totalAmount={taxSummary?.grandTotal || subtotal}
                              currencyCode={cart?.totalPrice?.currencyCode || 'USD'}
                              onCreatePaymentIntent={async () => {
                                // Wrapper to call handleCheckout and return the secret
                                // Note: We skip individual terms check here as PaymentInputForm handles it's own button disabled state
                                const result = await handleCheckout(true); // skipValidation=true (assuming already validated or re-validating)
                                return result;
                              }}
                              termsAccepted={termsAccepted}
                              setTermsAccepted={setTermsAccepted}
                              onPaymentSuccess={(paymentIntent) => {
                                const scopedKey = `cartId_${resolvedStoreKey}`;
                                localStorage.removeItem(scopedKey);
                                sessionStorage.removeItem(scopedKey);
                                navigate(`/${resolvedStoreKey}/order?payment_intent=${paymentIntent.id}`);
                              }}
                              onPaymentError={(paymentError) => {
                                // Check if we received an object (Stripe Error or JS Error)
                                if (paymentError && typeof paymentError === 'object') {
                                  const errorCode = paymentError.code;
                                  const declineCode = paymentError.decline_code;

                                  if (errorCode && isCardPaymentFailureEligibleForInvoice(errorCode, declineCode)) {
                                    setCardPaymentFailed(true);
                                  } else {
                                    // Fallback: show the error message from the object
                                    const msg = paymentError.message || paymentError.toString();
                                    setError(msg);
                                  }
                                } else {
                                  // Handle string errors or other types
                                  setError(paymentError || 'Payment processing failed');
                                }
                              }}
                            />
                          </Elements>
                        )}

                        {/* Pay Later (Invoice) Option */}
                        {/* cardPaymentFailed overrules card-only config & min-amount, but the
                           event-date cutoff is an absolute hard block for both paths. */}
                        {(isInvoicePaymentAvailable(cart, eventData) ||
                          (cardPaymentFailed && !isEventWithinInvoiceCutoff(eventData))) && (
                            <InvoicePaymentForm
                              expanded={paymentMethod === PAYMENT_METHOD.INVOICE}
                              onToggle={() => setPaymentMethod(PAYMENT_METHOD.INVOICE)}
                              processing={processingPayLater}
                              error={displayError}
                              termsAccepted={termsAccepted}
                              setTermsAccepted={setTermsAccepted}
                              onPayLater={async (email) => {
                                if (processingPayLater || processingPayment) return;

                                try {
                                  setProcessingPayLater(true);
                                  setError(null);
                                  
                                  // 1. Validate Discounts First (with simple retry for resilience)
                                  let validationResult;
                                  let retryCount = 0;
                                  const maxRetries = 2;
                                  
                                  while (retryCount <= maxRetries) {
                                    try {
                                      validationResult = await validateCartDiscounts();
                                      break;
                                    } catch (err) {
                                      if (retryCount === maxRetries) throw err;
                                      retryCount++;
                                      await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                                    }
                                  }

                                  if (validationResult.shouldStop) {
                                    // Re-calc tax if cart changed
                                    if (validationResult.cart) {
                                      setCart(validationResult.cart);
                                      if (address && addressSaved) {
                                        // Pass validated cart to avoid stale data
                                        await handleAddressSave(address, validationResult.cart);
                                      } else {
                                        setTaxSummary(null);
                                      }
                                    }
                                    // Set error message AFTER handleAddressSave
                                    if (validationResult.message) {
                                      setError(validationResult.message);
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                    setShowBillingForm(true); // Retreat to the previous stage

                                    // Abort
                                    setProcessingPayLater(false);
                                    return;
                                  }
                                  const latestCart = validationResult.cart;

                                  if (!latestCart) throw new Error('Failed to retrieve fresh cart data');

                                  // 2. Update Cart Email if provided
                                  if (email) {
                                    try {
                                      // Only update if different to avoid version conflicts if fast clicking?
                                      // But updateCart handles versions.
                                      await updateCart([{
                                        action: 'setCustomerEmail',
                                        email: email
                                      }]);
                                    } catch (_emailErr) {
                                      // Email update is non-critical — continue with default email on file
                                    }
                                  }

                                  // 3. Create Invoice Order
                                  // We need to get the cart AGAIN after updateCart because version changed
                                  const updatedCart = await getCart(); // Safe fetch

                                  // Get billing address from saved address state


                                  const response = await api.post(PLACE_ORDER_INVOICE, {
                                    cartId: updatedCart.id,
                                    cartVersion: updatedCart.version,
                                    invoiceConfig: eventData ? {
                                      paymentMethodsConfig: eventData.paymentMethodsConfig,
                                      invoiceMinThreshold: eventData.invoiceMinThreshold,
                                      invoiceCutoffDays: eventData.invoiceCutoffDays,
                                      startDate: eventData.startDate,
                                      cardPaymentFailed: cardPaymentFailed
                                    } : {
                                      cardPaymentFailed: cardPaymentFailed
                                    }
                                  });

                                  if (!response.ok) {
                                    const data = await response.json().catch(() => ({}));
                                    throw new Error(data.error || 'Failed to place invoice order');
                                  }

                                  const data = await response.json();

                                  const orderId = data.orderId;
                                  if (!orderId) {
                                    throw new Error('Order creation failed — no orderId returned');
                                  }

                                  sessionStorage.setItem('orderId', orderId);

                                  // Fetch the full order
                                  const fetchedOrder = await getOrderById(orderId);
                                  if (fetchedOrder) {
                                    // Create the Invoice Payment draft
                                    const paymentDraft = {
                                      amountPlanned: fetchedOrder.taxedPrice?.totalGross || fetchedOrder.totalPrice,
                                      paymentMethodInfo: {
                                        paymentInterface: '',
                                        method: PAYMENT_METHOD.INVOICE
                                      },
                                    };

                                    let payment;
                                    try {
                                      // 1. Create the payment
                                      const paymentRes = await api.post(PAYMENT_CREATE, paymentDraft);
                                      if (!paymentRes.ok) {
                                        throw new Error('Failed to create payment record');
                                      }
                                      payment = await paymentRes.json();
                                    } catch (payErr) {
                                      console.error('Invoice payment creation failed:', payErr);
                                      // Graceful degradation: The confirmation page will handle the custom field 
                                      // synchronization if attachment fails here.
                                      throw new Error(`Payment creation failed: ${payErr.message}`);
                                    }

                                    try {
                                      // 2. Attach payment AND update custom fields (Amber Country, Legal Entity) in ONE call
                                      await updateOrderCustomFields(fetchedOrder, [
                                        {
                                          action: ORDER_CT_ACTIONS.ADD_PAYMENT,
                                          payment: { typeId: ORDER_CT_TYPES.PAYMENT, id: payment.id }
                                        }
                                      ]);
                                    } catch (attachErr) {
                                      console.error('Failed to attach payment to order:', attachErr);
                                      // CRITICAL: Payment exists but is not attached — needs manual intervention signal
                                      throw new Error(`Payment created (${payment.id}) but attachment to order failed. Please contact support.`);
                                    }
                                  }

                                  const scopedKey = `cartId_${resolvedStoreKey}`;
                                  localStorage.removeItem(scopedKey);
                                  sessionStorage.removeItem(scopedKey);

                                  navigate(`/${resolvedStoreKey}/invoice-confirmation?orderId=${orderId}`);
                                } catch (err) {
                                  setError(err.message || 'Failed to place order with Pay Later');
                                } finally {
                                  setProcessingPayLater(false);
                                }
                              }}
                            />
                          )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Order Summary - Right Side - Visible only on Desktop (>= 576px) */}
            <div className="hidden min-[576px]:block checkout-right-column">
              <OrderSummary
                lineItems={transformedLineItems}
                subtotal={{ centAmount: Math.round(subtotalBeforeDiscount * 100), currencyCode: cart.currency || 'USD' }}
                totalDiscount={{ centAmount: Math.round(discountAmount * 100), currencyCode: cart.currency || 'USD' }}
                total={{
                  centAmount: Math.round(subtotal * 100),
                  currencyCode: cart.currency || 'USD'
                }}
                tax={getTaxForSummary(taxSummary, subtotal)}
                appliedDiscounts={getAppliedDiscountsForSummary(cart, discountAmount, subtotalBeforeDiscount)}
                showActiveCodes={true}
                disabled={loading}
                placeholder="Enter discount code"
                buttonText="Apply"
              />
            </div>
          </div>

        </div >
      </>
    </CheckoutDiscountProvider>
  );
};

export default CheckoutPage;